import React, { useCallback, useEffect, useRef, useState } from "react";
// @ts-ignore
import { StriimTypography } from "@striim/striim-ui-v2";
import { useAppSelector } from "src/stores/redux/hooks";
import { getStreamPreviewData } from "../../helpers/selectors";
import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import streamDataLoader from "../stream-data-loader";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import metaobjectConverter from "core/services/metaStoreService/metaobject-converter";

const cellRenderer = ({ value }: any) => {
    if (typeof value === "object" && Object.keys(value ?? {}).length > 0) {
        return (
            <StriimTypography variant="body4" color="greyscale.900">
                {JSON.stringify(value)}
            </StriimTypography>
        );
    }
    return (
        <StriimTypography variant="body4" color="greyscale.900">
            {value}
        </StriimTypography>
    );
};

const WIDE_FIELDS = ["data", "metadata"];

const getFieldColumnDef = (fieldName: string) => {
    const isWide = WIDE_FIELDS.includes(fieldName);
    return {
        flex: isWide ? 3 : 1,
        minWidth: isWide ? 240 : 120
    };
};

const StreamPreviewTable: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
    const data = useAppSelector(getStreamPreviewData);
    const [columns, setColumns] = useState<any[]>([]);
    const gridApiRef = useRef<any>(null);
    const prevLengthRef = useRef<number>(0);
    const prevVisibleRef = useRef<boolean>(false);

    // Fetch columns from dataType when preview becomes visible
    useEffect(() => {
        if (isVisible && !prevVisibleRef.current) {
            prevLengthRef.current = 0;
            setColumns([]);

            const shortName = streamDataLoader.streamName;
            if (!shortName) return;

            const streamFQN = metaobjectConverter.convertShortNameToFullName(shortName, "STREAM");

            metaStoreService.findById(streamFQN).then((stream: any) => {
                if (!stream || !stream.dataType) return;
                metaStoreService.findById(stream.dataType).then((type: any) => {
                    if (!type || !type.fields) return;
                    const cols = [
                        {
                            headerName: "",
                            field: "table_id",
                            resizable: true,
                            width: 80,
                            valueGetter: (params: any) => {
                                return params.data.table_id ? parseInt(params.data.table_id) + 1 : "";
                            },
                            cellRenderer
                        },
                        ...type.fields.map((field: any) => ({
                            headerName: field.name,
                            field: field.name,
                            resizable: true,
                            wrapText: true,
                            autoHeight: true,
                            cellRenderer,
                            ...getFieldColumnDef(field.name)
                        })),
                        type?.fields && type.fields.length === 0
                            ? {
                                  headerName: "data",
                                  field: "data",
                                  resizable: true,
                                  wrapText: true,
                                  autoHeight: true,
                                  cellRenderer,
                                  ...getFieldColumnDef("data")
                              }
                            : undefined
                    ];
                    setColumns(cols.filter(Boolean));
                });
            });
        }
        prevVisibleRef.current = isVisible;
    }, [isVisible]);

    const onGridReady = useCallback((params: any) => {
        gridApiRef.current = params.api;
    }, []);

    useEffect(() => {
        if (!gridApiRef.current) {
            return;
        }

        // Data was cleared (e.g. refresh) — flush the grid
        if (data.length === 0) {
            const allRows: any[] = [];
            gridApiRef.current.forEachNode((node: any) => allRows.push(node.data));
            if (allRows.length > 0) {
                gridApiRef.current.applyTransaction({ remove: allRows });
            }
            prevLengthRef.current = 0;
            return;
        }

        const newRows = data.slice(prevLengthRef.current);

        if (newRows.length > 0) {
            const rowsWithId = newRows.map((row: any, index: number) => ({
                ...row,
                table_id: `${prevLengthRef.current + index}`
            }));
            gridApiRef.current.applyTransaction({ add: rowsWithId });
            prevLengthRef.current = data.length;
        }
    }, [data]);

    return (
        <Box sx={{ height: "100%", width: "100%" }} className="ag-theme-material table-styles">
            <AgGridReact
                columnDefs={columns}
                onGridReady={onGridReady}
                getRowId={(params: any) => params.data.table_id}
            />
        </Box>
    );
};

export default StreamPreviewTable;
